/**
 * EIP-712 Permit Signature for USDC
 * 
 * Generates gasless transfer approvals for USDC on EVM chains.
 */

import { type Address, type Hash, parseUnits } from 'viem';

export interface PermitSignature {
    owner: Address;
    spender: Address;
    value: bigint;
    nonce: bigint;
    deadline: bigint;
    v: number;
    r: Hash;
    s: Hash;
}

const PERMIT_TYPES = {
    Permit: [
        { name: 'owner', type: 'address' },
        { name: 'spender', type: 'address' },
        { name: 'value', type: 'uint256' },
        { name: 'nonce', type: 'uint256' },
        { name: 'deadline', type: 'uint256' },
    ],
};

// USDC contract addresses per chain
const USDC_ADDRESSES: Record<number, Address> = {
    1: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',     // Ethereum
    8453: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',  // Base
    42161: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831', // Arbitrum
    137: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',   // Polygon
};

function getUSDCAddress(chainId: number): Address {
    return USDC_ADDRESSES[chainId] || USDC_ADDRESSES[1];
}

/**
 * Generate EIP-712 permit signature for gasless USDC transfer
 */
export async function generatePermitSignature(
    walletAddress: Address,
    spender: Address,
    amount: string,
    chainId: number,
    nonce: bigint,
    deadline: bigint,
    signTypedDataAsync: (args: any) => Promise<Hash>
): Promise<PermitSignature> {
    const domain = {
        name: 'USD Coin',
        version: '2',
        chainId,
        verifyingContract: getUSDCAddress(chainId),
    };

    const message = {
        owner: walletAddress,
        spender,
        value: parseUnits(amount, 6), // USDC has 6 decimals
        nonce,
        deadline,
    };

    const signature = await signTypedDataAsync({
        domain,
        types: PERMIT_TYPES,
        primaryType: 'Permit',
        message,
    });

    // Parse signature (r, s, v)
    const sigBytes = signature.slice(2);
    const r = `0x${sigBytes.slice(0, 64)}` as Hash;
    const s = `0x${sigBytes.slice(64, 128)}` as Hash;
    const v = parseInt(sigBytes.slice(128, 130), 16);

    return {
        owner: walletAddress,
        spender,
        value: message.value,
        nonce,
        deadline,
        v,
        r,
        s,
    };
}
